import { AbsoluteFill, Audio, Html5Video, Sequence, useVideoConfig } from "remotion";
import {
  getClipPlaybackRate,
  VIDEO_EDITOR_PREVIEW_ACCEPTABLE_TIME_SHIFT_SECONDS,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import { getClipTimelineDuration } from "./projectSchema";
import { getClipVolume } from "./clipProperties";
import {
  createFullCanvasClipLayout,
  layoutToLayerBoxStyle,
  repairLayoutAspectRatio,
} from "./clipLayout";

const ClipLayer = ({ clip, fps, compositionWidth, compositionHeight }) => {
  const durationInFrames = Math.max(
    1,
    Math.ceil(getClipTimelineDuration({ clip }) * fps)
  );
  const playbackRate = getClipPlaybackRate({ clip });
  const trimBefore = Math.max(0, Math.floor((clip.inPoint || 0) * fps));
  const trimAfter = Math.max(
    trimBefore + 1,
    Math.floor((clip.outPoint || 0) * fps)
  );
  const volume = getClipVolume(clip);

  if (clip.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO) {
    return (
      <Sequence
        from={Math.floor((clip.position || 0) * fps)}
        durationInFrames={durationInFrames}
      >
        <Audio
          src={clip.sourceUrl}
          trimBefore={trimBefore}
          playbackRate={playbackRate}
          volume={volume}
          acceptableTimeShiftInSeconds={
            VIDEO_EDITOR_PREVIEW_ACCEPTABLE_TIME_SHIFT_SECONDS
          }
        />
      </Sequence>
    );
  }

  if (clip.type !== VIDEO_EDITOR_TRACK_TYPE.VIDEO) {
    return null;
  }

  const layoutStyle = layoutToLayerBoxStyle(
    repairLayoutAspectRatio({
      layout: clip.layout,
      sourceWidth: clip.sourceWidth,
      sourceHeight: clip.sourceHeight,
      compositionWidth,
      compositionHeight,
      fallbackLayout: createFullCanvasClipLayout(),
    })
  );

  return (
    <Sequence
      from={Math.floor((clip.position || 0) * fps)}
      durationInFrames={durationInFrames}
    >
      <div
        data-video-editor-video-layer={clip.id}
        style={{
          ...layoutStyle,
          overflow: "hidden",
          backgroundColor: "transparent",
          zIndex: (Number(clip.layerIndex) || 0) + 1,
        }}
      >
        <Html5Video
          src={clip.sourceUrl}
          trimBefore={trimBefore}
          trimAfter={trimAfter}
          playbackRate={playbackRate}
          volume={volume}
          acceptableTimeShiftInSeconds={
            VIDEO_EDITOR_PREVIEW_ACCEPTABLE_TIME_SHIFT_SECONDS
          }
          pauseWhenBuffering
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            backgroundColor: "transparent",
          }}
        />
      </div>
    </Sequence>
  );
};

export const VideoEditorComposition = ({ edl }) => {
  const { fps } = useVideoConfig();
  const videoClips = Object.values(edl?.clips?.byId || {})
    .filter((clip) => clip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO)
    .sort(
      (left, right) =>
        (Number(left.layerIndex) || 0) - (Number(right.layerIndex) || 0)
    );
  const audioClips = Object.values(edl?.clips?.byId || {}).filter(
    (clip) => clip.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
  );

  return (
    <AbsoluteFill style={{ backgroundColor: "#fff" }}>
      {videoClips.map((clip) => (
        <ClipLayer
          key={clip.id}
          clip={clip}
          fps={fps}
          compositionWidth={edl?.compositionWidth}
          compositionHeight={edl?.compositionHeight}
        />
      ))}
      {audioClips.map((clip) => (
        <ClipLayer
          key={clip.id}
          clip={clip}
          fps={fps}
          compositionWidth={edl?.compositionWidth}
          compositionHeight={edl?.compositionHeight}
        />
      ))}
    </AbsoluteFill>
  );
};

export const VIDEO_EDITOR_COMPOSITION_ID = "VideoEditorComposition";
